/**
 * Meteo Background Cache
 *
 * Singleton that polls weather data + QNH every 5 minutes in the background.
 * On failure, retries with exponential backoff until success.
 * Components read from cache instantly — no waiting for network.
 */

import { useState, useEffect } from 'react'

// ── Types ──────────────────────────────────────────────────────────

export interface WeatherData {
  temp: string
  dew: string
  humidity: string
  windSpeed: string
  windGust: string
  windDir: string
  civilDawn: string
  civilDusk: string
}

export interface MeteoCache {
  weather: WeatherData | null
  qnh: string | null
  lastUpdated: number | null // timestamp ms
  weatherError: string | null
  qnhError: string | null
  loading: boolean // true only if we have NO cached data yet
}

const EMPTY_WEATHER: WeatherData = {
  temp: '—',
  dew: '—',
  humidity: '—',
  windSpeed: '—',
  windGust: '—',
  windDir: '—',
  civilDawn: '—',
  civilDusk: '—',
}

// ── Singleton State ────────────────────────────────────────────────

const POLL_INTERVAL = 5 * 60 * 1000 // 5 minutes
const RETRY_BASE_DELAY = 10 * 1000 // 10 seconds
const RETRY_MAX_DELAY = 60 * 1000 // 60 seconds

let cachedWeather: WeatherData | null = null
let cachedQnh: string | null = null
let lastUpdated: number | null = null
let weatherError: string | null = null
let qnhError: string | null = null

let pollTimer: ReturnType<typeof setInterval> | null = null
let weatherRetryTimer: ReturnType<typeof setTimeout> | null = null
let qnhRetryTimer: ReturnType<typeof setTimeout> | null = null
let weatherRetryCount = 0
let qnhRetryCount = 0

let subscribers: Set<() => void> = new Set()
let isPollingStarted = false

function notifySubscribers() {
  subscribers.forEach((cb) => cb())
}

// ── XML Parsing (extracted from original Meteo.tsx) ────────────────

function parseWeatherXml(text: string): WeatherData {
  const parser = new DOMParser()
  let xmlDoc = parser.parseFromString(text, 'text/xml')

  const parseError = xmlDoc.querySelector('parsererror')
  if (parseError) {
    // Fallback: try parsing as HTML first, then extract XML
    const htmlDoc = parser.parseFromString(text, 'text/html')
    const body = htmlDoc.body?.textContent?.trim()
    if (body) {
      const xmlDoc2 = parser.parseFromString(body, 'text/xml')
      if (!xmlDoc2.querySelector('parsererror')) {
        xmlDoc = xmlDoc2
      } else {
        throw new Error('XML parse error')
      }
    } else {
      throw new Error('XML parse error')
    }
  }

  const root = xmlDoc.documentElement
  const sensors = xmlDoc.getElementsByTagName('sensor')
  const sensorMap: Record<string, string> = {}

  for (let i = 0; i < sensors.length; i++) {
    const sensor = sensors[i]
    const id = sensor.getElementsByTagName('id')[0]?.textContent?.trim() || ''
    const value =
      sensor.getElementsByTagName('value')[0]?.textContent?.trim() || ''
    if (id) sensorMap[id] = value
  }

  const getById = (id: string): string | null => sensorMap[id] || null

  const getByTag = (name: string): string | null => {
    const els = xmlDoc.getElementsByTagName(name)
    if (els.length > 0) {
      const text = els[0].textContent?.trim()
      if (text) return text
      return els[0].getAttribute('value') || els[0].getAttribute('val') || null
    }
    return null
  }

  const windUnitRaw =
    getByTag('windunit') ||
    getByTag('wunit') ||
    getByTag('windunits') ||
    getByTag('speedunit') ||
    ''
  const windUnit = windUnitRaw.toLowerCase()

  const convertToKnots = (val: string | null): string => {
    if (!val) return '—'
    const num = parseFloat(val.replace(',', '.'))
    if (isNaN(num)) return val
    let knots: number
    if (
      windUnit.includes('km') ||
      windUnit.includes('kmh') ||
      windUnit.includes('kph')
    ) {
      knots = num / 1.852
    } else if (windUnit.includes('mph') || windUnit.includes('mi')) {
      knots = num / 1.151
    } else if (
      windUnit.includes('m/s') ||
      windUnit.includes('ms') ||
      windUnit.includes('mps')
    ) {
      knots = num * 1.944
    } else {
      knots = num / 1.852
    }
    return Math.round(knots).toString()
  }

  const formatTemp = (val: string | null): string => {
    if (!val) return '—'
    const num = parseFloat(val.replace(',', '.'))
    if (isNaN(num)) return val
    return Math.round(num).toString()
  }

  const formatNum = (val: string | null): string => {
    if (!val) return '—'
    const num = parseFloat(val.replace(',', '.'))
    if (isNaN(num)) return val
    return Math.round(num).toString()
  }

  const formatTime = (val: string | null): string => {
    if (!val || val === '—') return '—'
    const parts = val.split(':')
    if (parts.length !== 2) return val
    const h = parts[0].trim().padStart(2, '0')
    const m = parts[1].trim().padStart(2, '0')
    return `${h}:${m} CET`
  }

  return {
    temp: formatTemp(getById('1006')),
    dew: formatTemp(getById('1010')),
    humidity: formatNum(getById('1007')),
    windSpeed: convertToKnots(getById('1003')),
    windGust: convertToKnots(getById('1004')),
    windDir: formatNum(getById('1002')),
    civilDawn: formatTime(getByTag('civstart')),
    civilDusk: formatTime(getByTag('civend')),
  }
}

function parseQnhFromHtml(html: string): string | null {
  const match = html.match(/Q(\d{4})/)
  return match ? match[1] : null
}

// ── Fetch Functions with Retry ─────────────────────────────────────

async function fetchWeather() {
  try {
    console.log('[METEO-CACHE] Fetching weather data...')
    const response = await fetch(
      'https://api.allorigins.win/raw?url=' +
        encodeURIComponent('http://93.99.194.142:2358/xml.xml'),
    )
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const text = await response.text()
    const data = parseWeatherXml(text)

    cachedWeather = data
    weatherError = null
    weatherRetryCount = 0
    lastUpdated = Date.now()
    console.log('[METEO-CACHE] Weather data cached successfully')
    notifySubscribers()
  } catch (err: any) {
    console.error('[METEO-CACHE] Weather fetch failed:', err?.message)
    weatherError = err?.message || 'Chyba načítání'
    weatherRetryCount++
    notifySubscribers()
    scheduleWeatherRetry()
  }
}

async function fetchQnh() {
  try {
    console.log('[METEO-CACHE] Fetching QNH from LKPR...')
    const response = await fetch(
      'https://api.allorigins.win/raw?url=' +
        encodeURIComponent('https://meteo.rlp.cz/xLKPR_metrep.htm'),
    )
    if (!response.ok) throw new Error(`QNH HTTP ${response.status}`)
    const html = await response.text()
    const qnh = parseQnhFromHtml(html)

    if (qnh) {
      cachedQnh = qnh
      qnhError = null
      qnhRetryCount = 0
      console.log('[METEO-CACHE] QNH cached:', qnh)
    } else {
      console.warn('[METEO-CACHE] QNH not found in LKPR page')
      qnhError = 'QNH nenalezeno'
      qnhRetryCount++
      scheduleQnhRetry()
    }
    notifySubscribers()
  } catch (err: any) {
    console.error('[METEO-CACHE] QNH fetch failed:', err?.message)
    qnhError = err?.message || 'Chyba načítání QNH'
    qnhRetryCount++
    notifySubscribers()
    scheduleQnhRetry()
  }
}

function scheduleWeatherRetry() {
  if (weatherRetryTimer) clearTimeout(weatherRetryTimer)
  const delay = Math.min(
    RETRY_BASE_DELAY * Math.pow(2, weatherRetryCount - 1),
    RETRY_MAX_DELAY,
  )
  console.log(
    `[METEO-CACHE] Weather retry #${weatherRetryCount} in ${delay / 1000}s`,
  )
  weatherRetryTimer = setTimeout(() => {
    weatherRetryTimer = null
    fetchWeather()
  }, delay)
}

function scheduleQnhRetry() {
  if (qnhRetryTimer) clearTimeout(qnhRetryTimer)
  const delay = Math.min(
    RETRY_BASE_DELAY * Math.pow(2, qnhRetryCount - 1),
    RETRY_MAX_DELAY,
  )
  console.log(`[METEO-CACHE] QNH retry #${qnhRetryCount} in ${delay / 1000}s`)
  qnhRetryTimer = setTimeout(() => {
    qnhRetryTimer = null
    fetchQnh()
  }, delay)
}

function fetchAll() {
  // Clear any pending retries — fresh poll cycle
  if (weatherRetryTimer) {
    clearTimeout(weatherRetryTimer)
    weatherRetryTimer = null
  }
  if (qnhRetryTimer) {
    clearTimeout(qnhRetryTimer)
    qnhRetryTimer = null
  }
  weatherRetryCount = 0
  qnhRetryCount = 0
  fetchWeather()
  fetchQnh()
}

// ── Polling Control ────────────────────────────────────────────────

function startPolling() {
  if (isPollingStarted) return
  isPollingStarted = true
  console.log('[METEO-CACHE] Starting background polling (every 5 min)')

  // Fetch immediately on first start
  fetchAll()

  // Then poll every 5 minutes
  pollTimer = setInterval(() => {
    console.log('[METEO-CACHE] Scheduled poll triggered')
    fetchAll()
  }, POLL_INTERVAL)
}

// ── React Hook ─────────────────────────────────────────────────────

export function useMeteoCache(): MeteoCache {
  const [, forceUpdate] = useState(0)

  useEffect(() => {
    // Subscribe to cache updates
    const cb = () => forceUpdate((n) => n + 1)
    subscribers.add(cb)

    // Start polling if not already started (singleton — only starts once)
    startPolling()

    return () => {
      subscribers.delete(cb)
    }
  }, [])

  const hasCachedData = cachedWeather !== null

  return {
    weather: cachedWeather,
    qnh: cachedQnh,
    lastUpdated,
    weatherError: hasCachedData ? null : weatherError, // suppress error if we have stale data
    qnhError: cachedQnh ? null : qnhError,
    loading: !hasCachedData, // only "loading" if we have zero cached data
  }
}
